import type { BrandNoteDTO, GetBrandDTO } from "@nlc/shared-types";

import { FileTextIcon } from "lucide-react";

import { Badge } from "@/components/badge";
import { Skeleton } from "@/components/skeleton";
import { TiptapEditor } from "@/components/ui/TiptapEditor";
import { CreatorTagBadge } from "@/features/brands/edit/components/knowledge/components/CreatorTagBadge";
import { NoteTypeBadge } from "@/features/brands/edit/components/knowledge/components/NoteTypeBadge";
import { useBrandNotes } from "@/features/brands/edit/components/knowledge/hooks/useBrandNotes";
import { useKnowledgeFilters } from "@/features/brands/edit/components/knowledge/hooks/useKnowledgeFilters";
import { cn } from "@/utils/cn";
import { dateFormatter } from "@/utils/dateFormatter";

import { SectionHeader } from "../SectionHeader";

type KnowledgeTabProps = {
  brand: Pick<GetBrandDTO, "id">;
};

export function KnowledgeTab({ brand }: KnowledgeTabProps) {
  const {
    data: notes,
    isError,
    isLoading,
  } = useBrandNotes({
    brandId: brand.id,
  });
  const { filteredNotes, selectedTab, setSelectedTab, tabItems } =
    useKnowledgeFilters({ notes });

  const totalNotes = tabItems[0]?.count ?? 0;

  return (
    <div className="flex flex-col gap-xl">
      <SectionHeader Icon={FileTextIcon} title="Brand notes" />

      {isLoading ? (
        <KnowledgeFilterTabsSkeleton />
      ) : !isError && totalNotes > 0 ? (
        <KnowledgeFilterTabs
          onSelect={setSelectedTab}
          selectedTab={selectedTab}
          tabItems={tabItems}
        />
      ) : null}

      {isLoading ? (
        <div
          aria-label="Loading..."
          className="flex flex-col gap-md"
          role="status"
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton className="h-32 w-full rounded-2xl" key={index} />
          ))}
        </div>
      ) : isError ? (
        <p className="py-xl text-center text-sm text-text-quaternary">
          Failed to load brand notes.
        </p>
      ) : filteredNotes.length === 0 ? (
        <p className="py-xl text-center text-sm text-text-quaternary">
          {selectedTab === "all"
            ? "No brand notes available"
            : "No brand notes match this filter."}
        </p>
      ) : (
        <ul className="flex flex-col gap-md">
          {filteredNotes.map((note) => (
            <KnowledgeNoteCard key={note.id} note={note} />
          ))}
        </ul>
      )}
    </div>
  );
}

function KnowledgeFilterTabs({
  onSelect,
  selectedTab,
  tabItems,
}: {
  onSelect: (id: string) => void;
  selectedTab: string;
  tabItems: { count: number; id: string; label: string }[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-xs">
      {tabItems.map((item) => {
        const isActive = selectedTab === item.id;

        return (
          <button
            aria-pressed={isActive}
            className={cn(
              "flex h-9 cursor-pointer items-center gap-md rounded-sm px-lg py-md text-sm font-semibold transition-colors",
              isActive
                ? "bg-bg-brand-primary-alt text-text-brand-secondary"
                : "text-text-quaternary hover:bg-bg-secondary",
            )}
            key={item.id}
            onClick={() => onSelect(item.id)}
            type="button"
          >
            {item.label}
            <Badge
              isRounded
              size="small"
              variant={isActive ? "brand" : "primary"}
            >
              {item.count}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}

function KnowledgeFilterTabsSkeleton() {
  const widths = ["w-20", "w-36", "w-32", "w-28"];

  return (
    <div
      aria-hidden
      className="flex flex-wrap items-center gap-xs"
      role="presentation"
    >
      {widths.map((width, index) => (
        <Skeleton className={cn("h-9 rounded-sm", width)} key={index} />
      ))}
    </div>
  );
}

function KnowledgeNoteCard({ note }: { note: BrandNoteDTO }) {
  return (
    <li className="flex flex-col gap-xs rounded-2xl border border-border-primary bg-bg-secondary p-xl">
      <h3 className="text-lg font-semibold text-text-primary">{note.name}</h3>

      <div className="flex flex-col gap-xs">
        <p className="text-xs font-medium text-text-secondary">
          Last edited on {dateFormatter(note.updatedAt ?? note.createdAt)}
          {note.creatorName ? `, by ${note.creatorName}` : ""}
        </p>
        <div className="flex flex-wrap items-center gap-md">
          <CreatorTagBadge isCustomerNote={note.isCustomerNote} />
          <NoteTypeBadge noteType={note.noteType} />
        </div>
      </div>

      <TiptapEditor
        borderless
        isReadOnly
        key={note.updatedAt ?? note.createdAt}
        value={note.description}
      />
    </li>
  );
}
